import { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import {
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";
import { FormControl, FormField, FormItem, FormMessage } from "./form";
import { cn } from "@drpcrd/utils/shared";

type Props<TFieldValues extends FieldValues = FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label: ReactNode;
  type: "datetime-local" | "date";
} & InputHTMLAttributes<HTMLInputElement>;

export const InputDatePicker = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  type,
}: Props<TFieldValues>) => {
  const handleDateChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
  ) => {
    const rawValue = e.target.value;
    if (!rawValue) {
      field.onChange(null);
      return;
    }

    if (type === "date") {
      field.onChange(new Date(rawValue).toISOString());
      return;
    }

    const [datePart, timePart] = rawValue.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);

    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));

    field.onChange(utcDate);
  };

  const handleValue = (
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
  ): string => {
    if (!field.value) {
      return "";
    }

    const parsedDate = new Date(field.value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    if (type === "datetime-local") {
      return parsedDate.toISOString().slice(0, 16);
    }

    return parsedDate.toISOString().split("T")[0];
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className="mt-4 w-full">
          <FormControl>
            <label className="flex w-full flex-row items-center gap-3">
              <div
                className={cn(
                  "w-20 text-sm md:text-lg",
                  error?.message && "text-pink-500",
                )}
              >
                {label}
                <FormMessage className="text-pink-500" />
              </div>

              <input
                value={handleValue(field)}
                onChange={(e) => handleDateChange(e, field)}
                type={type}
                className="grow rounded-lg border bg-white px-2 py-[10px] font-sans text-lg font-normal lg:w-4/12 lg:grow-0"
              />
            </label>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
